import { useState } from 'react';
import { MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { PRIVACY_CEILINGS, type Member, type PrivacyCeilingName } from '@cef-ai/vault-sdk';
import { setOrgMemberCeiling } from '@/lib/clients/vault/org-members';
import { isPrivacyCeilingName, type MemberCeiling } from '@/lib/clients/vault/orgs';
import { stepUpRequiredError, toDisplayError } from '@/lib/vault/step-up-error';
import { useVaultSession } from '@/src/state/session';
import '@/src/styles/orgs.css';

const CEILING_LABEL: Record<PrivacyCeilingName, string> = {
  public: 'Public',
  internal: 'Internal',
  private: 'Private',
  restricted: 'Restricted',
};

/** How the control spells the current value when no ceiling is shown. */
const UNSIGNED_LABEL: Record<Exclude<MemberCeiling, PrivacyCeilingName>, string> = {
  none: 'None signed',
  unknown: 'Unknown',
};

interface Props {
  /** The vault the ceiling write addresses — the section's selected context. */
  vaultId: string;
  /** The roster row, whose standing the re-signed grant document rebuilds from. */
  member: Pick<Member, 'memberPubkey' | 'role' | 'expiresAt'>;
  /** The member's SIGNED ceiling as read off the signed roster. */
  ceiling: MemberCeiling;
  /** Fired once the write commits, so the section can re-read the ceilings. */
  onSaved: () => void;
}

/**
 * One roster row's privacy-ceiling picker. The roster row does NOT carry the
 * ceiling on the wire, so the current value shown here is the SIGNED one and
 * the control offers exactly the four ceilings — no "clear": with the option
 * unset the SDK signs a v1 document unconditionally, and the server's
 * omit-vs-clear semantic for a member who already holds a ceiling is
 * undecided — a downgrade is a server-semantics follow-up this UI must not
 * promise.
 *
 * Choosing a ceiling RE-SIGNS the member's whole grant (`memberships.put`
 * with the row's standing and the ceiling option), and the write runs inside
 * the choice click: the ceremony-surface gate is a synchronous read at the
 * top of that click, and the signing handle — whose ceremony, if any, must
 * open within this gesture — resolves before the document-bearing write. An
 * invited admin's write is server-refused (their key is not the org owner's);
 * the refusal is surfaced as the server states it, not hidden.
 */
export function MemberCeilingPicker({ vaultId, member, ceiling, onSaved }: Props) {
  const session = useVaultSession();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = isPrivacyCeilingName(ceiling) ? ceiling : '';
  const display = isPrivacyCeilingName(ceiling) ? CEILING_LABEL[ceiling] : UNSIGNED_LABEL[ceiling];

  function handleChange(e: SelectChangeEvent) {
    const choice = e.target.value as PrivacyCeilingName;
    // Re-selecting the current value closes the menu and nothing else; there
    // is no write to make, because the signed document already says this.
    if (choice === selected) return;
    // Gate BEFORE any async work, synchronously inside the click that will
    // sign: if the ceremony would need a popup the browser will block, now —
    // inside the gesture — is when to find out.
    if (session.wallet.ceremonySurface() === 'popup') {
      setError(toDisplayError(stepUpRequiredError()));
      return;
    }
    setSubmitting(true);
    setError(null);
    void (async () => {
      try {
        await session.signingHandle();
        await setOrgMemberCeiling(session.sdk, vaultId, member, choice);
        onSaved();
      } catch (e) {
        setError(toDisplayError(e));
      } finally {
        setSubmitting(false);
      }
    })();
  }

  return (
    <span className="vault-orgs__ceiling">
      <span className="vault-orgs__ceiling-label">Privacy</span>
      <Select
        size="small"
        className="vault-orgs__ceiling-select"
        // MUI routes extra Select props to the input ROOT, not the combobox
        // div; SelectDisplayProps is the one channel that names the control.
        SelectDisplayProps={{ 'aria-label': `Privacy ceiling for ${member.memberPubkey}` }}
        title="The most sensitive memory class this member may see — signed by the vault owner together with the member's whole grant."
        value={selected}
        disabled={submitting}
        onChange={handleChange}
        // displayEmpty: "None signed"/"Unknown" ARE the current value — MUI
        // would otherwise blank the control instead of rendering them.
        displayEmpty
        renderValue={() => display}
      >
        {PRIVACY_CEILINGS.map((name) => (
          <MenuItem key={name} value={name}>{CEILING_LABEL[name]}</MenuItem>
        ))}
      </Select>
      {error && <span className="vault-orgs__ceiling-error" role="alert">{error}</span>}
    </span>
  );
}
